import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, FormikProps } from 'formik'
import * as yup from 'yup'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'

import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'

import TextField from '@/components/formik/fields/TextField'
import ActsAutoCompleteComponent, { ActAutocompleteOption } from '@/components/ui/autocomplete/ActsAutoComplete'
import { useGetActsQuery } from '@/core/services/acts'
import { Trans } from '@lingui/macro'

export type SimpleActFormValues = {
    selectedAct?: ActAutocompleteOption
    supplement?: string
}

export type SimpleActDto = {
    libelle: string
    honoraire: string
    id_transposition: string
    supplement: string
}

export type SimpleDesmosActSelectionDialogProps = {
    onClose?: () => void
    onSelectAct?: (actValues: SimpleActDto) => void
    open: boolean
    title?: string
    description?: string
}

const actFormValidationSchema = yup.object().shape({
    selectedAct: yup.object().required('Please select an act').nullable(),
    supplement: yup.string().optional(),
})

export function SimpleDesmosActSelectionDialog(props: SimpleDesmosActSelectionDialogProps) {
    const { onClose, onSelectAct, open, title, description } = props
    const { t } = useTranslation()
    const formRef = useRef<FormikProps<SimpleActFormValues>>(null)

    const { data: acts = [] } = useGetActsQuery({})

    const handleSelectAct = () => {
        formRef.current?.submitForm()
    }

    const handleActChange = (event: any, newValue: (string | ActAutocompleteOption)[] | ActAutocompleteOption | string | null, reason: string) => {
        if (newValue && typeof newValue === 'object' && 'libelle' in newValue) {
            formRef.current?.setFieldValue('selectedAct', newValue)
        } else {
            formRef.current?.setFieldValue('selectedAct', null)
        }
    }

    const handleSubmit = (values: SimpleActFormValues) => {
        if (!values.selectedAct) return

        const actValues = {
            libelle: values.selectedAct.libelle,
            honoraire: values.selectedAct.honoraire,
            id_transposition: values.selectedAct.id_transposition,
            supplement: values.supplement || '0',
        } as SimpleActDto

        onSelectAct?.(actValues)
    }

    return (
        <Dialog
            onClose={onClose}
            open={open}
            PaperProps={{
                sx: {
                    maxWidth: '600px',
                    width: '100%',
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>{title || t('page.desmosAct.selectAct.title')}</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Alert severity="info" icon={<InfoIcon />}>
                        <Typography variant="body2">
                            {description || t('page.desmosAct.selectAct.description')}
                        </Typography>
                    </Alert>

                    <Formik
                        innerRef={formRef}
                        initialValues={{ selectedAct: undefined, supplement: '0' }}
                        onSubmit={handleSubmit}
                        validationSchema={actFormValidationSchema}
                    >
                        {({ values }) => (
                            <Form>
                                <Stack spacing={2}>
                                    <ActsAutoCompleteComponent
                                        id="acts-autocomplete"
                                        name="selectedAct"
                                        label={t('field.desmosAct.selectAct.label', { defaultValue: 'Select an act' })}
                                        editable={true}
                                        selectedActs={values.selectedAct || null}
                                        acts={acts}
                                        multiple={false}
                                        onActChange={handleActChange}
                                    />

                                    {values.selectedAct && (
                                        <Alert severity="success">
                                            <Typography variant="body2">
                                                <strong>{t('common.selected')}:</strong> {values.selectedAct.libelle}<br />
                                                <strong>{t('common.code')}:</strong> {values.selectedAct.id_transposition}<br />
                                                <strong>{t('common.honorarium')}:</strong> {values.selectedAct.honoraire}€
                                            </Typography>
                                        </Alert>
                                    )}

                                    <TextField
                                        fullWidth
                                        label={t('field.desmosAct.supplement.label')}
                                        name="supplement"
                                        placeholder="0"
                                    />
                                </Stack>
                            </Form>
                        )}
                    </Formik>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button onClick={onClose} variant="outlined">
                    <Trans>Cancel</Trans>
                </Button>
                <LoadingButton onClick={handleSelectAct} variant="contained">
                    <Trans>Continue</Trans>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default SimpleDesmosActSelectionDialog

